import React, { useEffect, useRef, useState } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, useDisclosure, Badge, Flex, Text, Box,
  HStack, Select, Input, useToast,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import deportesApi, { Convocatoria } from '../../../api/deportesApi';
import FormularioConvocatoria, { ESTATUS_CONVOCATORIA } from './FormularioConvocatoria';
import ModalInscripciones from './ModalInscripciones';
import { useCatalogos, Paginacion, fmtFecha, ROJO, ROJO_HOVER } from '../_shared';

const ESTATUS_COLOR: Record<string, string> = {
  Borrador: 'gray', Programada: 'cyan', Vigente: 'green', Cerrada: 'blue', Cancelada: 'red',
};

const TablaConvocatorias: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { disciplinas } = useCatalogos();

  const { isOpen: isVer, onOpen: onVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isEditar, onOpen: onEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isNuevo, onOpen: onNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isInsc, onOpen: onInsc, onClose: onCloseInsc } = useDisclosure();
  const { isOpen: isEliminar, onOpen: onEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [sel, setSel] = useState<Convocatoria | null>(null);
  const [data, setData] = useState<Convocatoria[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [fNombre, setFNombre] = useState('');
  const [fDisciplina, setFDisciplina] = useState('');
  const [fEstatus, setFEstatus] = useState('');
  const [fPortal, setFPortal] = useState('');

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await deportesApi.listarConvocatorias({
        nombre: fNombre, disciplinaId: fDisciplina, estatus: fEstatus,
        publicadaPortal: fPortal || undefined,
      });
      setData(res || []);
    } catch { setData([]); } finally { setCargando(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1); fetchData(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fNombre, fDisciplina, fEstatus, fPortal]);

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = data.slice(indexPrimero, indexUltimo);

  const abrir = async (item: Convocatoria, accion: 'ver' | 'editar' | 'insc') => {
    try { setSel(await deportesApi.obtenerConvocatoria(item.id)); }
    catch { setSel(item); }
    if (accion === 'ver') onVer();
    else if (accion === 'editar') onEditar();
    else onInsc();
  };

  const handleGuardado = () => { onCloseNuevo(); onCloseEditar(); fetchData(); };

  const togglePortal = async (c: Convocatoria) => {
    try {
      if (c.publicadaPortal) await deportesApi.despublicarConvocatoria(c.id);
      else await deportesApi.publicarConvocatoria(c.id);
      toast({ title: c.publicadaPortal ? 'Convocatoria retirada del portal' : 'Convocatoria publicada en el portal', status: 'success', duration: 2500 });
      fetchData();
    } catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };

  const handleEliminar = async () => {
    if (!sel) return;
    setProcesando(true);
    try {
      await deportesApi.eliminarConvocatoria(sel.id);
      toast({ title: 'Convocatoria eliminada', status: 'success', duration: 3000 });
      onCloseEliminar(); fetchData();
    } catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 4000 }); }
    finally { setProcesando(false); }
  };

  const exportExcel = () => {
    const filas = data.map((c) => ({
      Folio: c.folio || '', Nombre: c.nombre, Disciplina: c.disciplina?.nombre || '',
      Publicación: fmtFecha(c.fechaPublicacion), Cierre: fmtFecha(c.fechaCierre),
      Responsable: c.responsable || '', Portal: c.publicadaPortal ? 'Sí' : 'No', Estatus: c.estatus || '',
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Convocatorias');
    XLSX.writeFile(wb, `Convocatorias_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={2} flexWrap="wrap">
          <Input placeholder="Buscar por nombre..." value={fNombre} onChange={(e) => setFNombre(e.target.value)} size="sm" w={{ base: 'full', md: '200px' }} />
          <Select placeholder="Disciplina" value={fDisciplina} onChange={(e) => setFDisciplina(e.target.value)} size="sm" w="150px">
            {disciplinas.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </Select>
          <Select placeholder="Estatus" value={fEstatus} onChange={(e) => setFEstatus(e.target.value)} size="sm" w="140px">
            {ESTATUS_CONVOCATORIA.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Select placeholder="Portal" value={fPortal} onChange={(e) => setFPortal(e.target.value)} size="sm" w="140px">
            <option value="true">Publicadas</option>
            <option value="false">No publicadas</option>
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={exportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={onNuevo}>Nueva convocatoria</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={ROJO}>
            <Tr>
              <Th color="white" fontSize="xs">Folio</Th>
              <Th color="white" fontSize="xs">Nombre</Th>
              <Th color="white" fontSize="xs">Disciplina</Th>
              <Th color="white" fontSize="xs">Publicación</Th>
              <Th color="white" fontSize="xs">Cierre</Th>
              <Th color="white" fontSize="xs">Portal</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.400">No se encontraron convocatorias</Text></Td></Tr>
            ) : itemsPagina.map((c, idx) => (
              <Tr key={c.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                <Td fontSize="xs">{c.folio || '—'}</Td>
                <Td maxW="220px"><Text noOfLines={1} fontSize="sm" fontWeight="medium">{c.nombre}</Text></Td>
                <Td>{c.disciplina?.nombre || '—'}</Td>
                <Td fontSize="xs">{fmtFecha(c.fechaPublicacion)}</Td>
                <Td fontSize="xs">{fmtFecha(c.fechaCierre)}</Td>
                <Td><Badge colorScheme={c.publicadaPortal ? 'green' : 'gray'}>{c.publicadaPortal ? 'Publicada' : 'No'}</Badge></Td>
                <Td><Badge colorScheme={ESTATUS_COLOR[c.estatus || ''] || 'gray'} borderRadius="full" px={2}>{c.estatus}</Badge></Td>
                <Td>
                  <HStack spacing={1} flexWrap="wrap">
                    <Button size="xs" colorScheme="blue" variant="outline" onClick={() => abrir(c, 'ver')}>Ver</Button>
                    <Button size="xs" colorScheme="orange" variant="outline" onClick={() => abrir(c, 'editar')}>Editar</Button>
                    <Button size="xs" colorScheme="purple" variant="outline" onClick={() => abrir(c, 'insc')}>Inscripciones</Button>
                    <Button size="xs" colorScheme={c.publicadaPortal ? 'gray' : 'green'} variant="solid" onClick={() => togglePortal(c)}>
                      {c.publicadaPortal ? 'Despublicar' : 'Publicar'}
                    </Button>
                    <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSel(c); onEliminar(); }}>Eliminar</Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Paginacion currentPage={currentPage} totalPages={totalPages} total={data.length} indexPrimero={indexPrimero} indexUltimo={indexUltimo} onChange={setCurrentPage} />

      <Modal isOpen={isVer} onClose={onCloseVer} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Detalle de la Convocatoria</ModalHeader>
          <ModalBody py={5}>{sel && <FormularioConvocatoria modo="view" data={sel} />}</ModalBody>
          <ModalFooter><Button onClick={onCloseVer}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isEditar} onClose={onCloseEditar} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Editar Convocatoria</ModalHeader>
          <ModalBody py={5}>{sel && <FormularioConvocatoria modo="edit" data={sel} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}</ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isNuevo} onClose={onCloseNuevo} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Nueva Convocatoria</ModalHeader>
          <ModalBody py={5}><FormularioConvocatoria modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} /></ModalBody>
        </ModalContent>
      </Modal>

      <ModalInscripciones isOpen={isInsc} onClose={onCloseInsc} convocatoria={sel} />

      <AlertDialog isOpen={isEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={ROJO}>Eliminar convocatoria</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar la convocatoria <strong>{sel?.nombre}</strong>?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaConvocatorias;
